import { EstimateDocumentRenderer } from '@/components/documents/estimates/blocks/EstimateDocumentRenderer';
import { MembershipOfferBlock } from '@/components/organisms/estimates/MembershipOfferBlock';
import { ProposalMembershipBenefits } from './ProposalMembershipBenefits';
import { ProposalOptionalSelection } from './ProposalOptionalSelection';
import { WisetackPublicCta } from './WisetackPublicCta';
import type { ProposalOptionDocumentProps } from './ProposalOptionDocument';
import {
  buildProposalOptionEstimate,
  type ProposalOptionDocumentSource,
} from '@/lib/sales/proposalOptionEstimate';
import { singleScopeThemeVars } from '@/lib/quote/singleScopeTheme';
import { proposalOptionOffers, splitProposalLines } from '@/lib/sales/proposalOptionBreakdown';

export interface ProposalItemizedOptionDocumentProps extends ProposalOptionDocumentProps {
  proposal: ProposalOptionDocumentSource;
}

/** One comparison tab uses the same authored blocks as an individual estimate.
 * Approval stays with the hosting page; choices use the proposal's existing math. */
export function ProposalItemizedOptionDocument(props: ProposalItemizedOptionDocumentProps) {
  const { proposal, option, offer, membershipSelected, displayTotal } = props;
  const { estimate, layout, totalsOverride } = buildProposalOptionEstimate({
    ...props,
    proposal: {
      ...proposal,
      customer: props.customer ?? proposal.customer,
      notes: props.notes === undefined ? proposal.notes : props.notes,
    },
  });
  const company = proposal.company
    ? { ...proposal.company, name: proposal.company.name ?? 'Your provider' }
    : null;
  const includedMemberships = splitProposalLines(option).required.filter(
    (line) => line.membershipBenefits,
  );

  return (
    <div style={singleScopeThemeVars(layout.branding)}>
      <EstimateDocumentRenderer
        layout={layout}
        estimate={estimate}
        company={company}
        attachmentImages={(props.attachments ?? []).map((attachment) => ({
          id: attachment.id,
          url: attachment.downloadUrl,
          fileName: attachment.fileName,
        }))}
        renderSlots={false}
        membershipOffer={offer}
        showMemberPrice={membershipSelected && offer?.planPrice != null}
        totalsOverride={totalsOverride}
        respectLinePriceVisibility
        timeZone={proposal.timezone}
        beforeTotals={
          <>
            {includedMemberships.map((line, index) => (
              <div key={line.lineKey ?? `${line.lineItemId}-${index}`} className="my-5">
                {includedMemberships.length > 1 ? (
                  <h3 className="text-sm font-semibold">{line.name}</h3>
                ) : null}
                <ProposalMembershipBenefits lineItem={line} />
              </div>
            ))}
            {props.canToggleOptionals &&
            props.onToggleOptional &&
            proposalOptionOffers(option).length > 0 ? (
              <div className="my-5">
                <h3 className="mb-2 text-sm font-semibold">Optional add-ons</h3>
                <ProposalOptionalSelection
                  option={option}
                  disabled={props.optionalsDisabled}
                  onToggleOptional={props.onToggleOptional}
                />
              </div>
            ) : null}
            {offer ? (
              <div className="my-5">
                <MembershipOfferBlock
                  offer={offer}
                  selected={membershipSelected}
                  onSelectedChange={props.onMembershipSelectedChange}
                  selectedBillingOptionId={props.billingOptionId}
                  onBillingOptionChange={props.onMembershipBillingChange}
                  variant="banner"
                  compact
                  className="w-full"
                />
              </div>
            ) : null}
          </>
        }
      />
      {props.showFinancing &&
      option.wisetack &&
      Number.isFinite(displayTotal) &&
      displayTotal >= 500 ? (
        <div className="mt-6">
          <WisetackPublicCta
            wisetack={option.wisetack}
            description="Prequalify to see available payment options before choosing this proposal option."
          />
        </div>
      ) : null}
    </div>
  );
}
